import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import BuildIcon from "@mui/icons-material/Build";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import "./AllNotificationsPage.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";

const BASE_URL = "https://estbn.com/EstbnNew/public/api";

const getNotificationIcon = (type, imageUrl) => {
  if (imageUrl) {
    return (
      <img src={imageUrl} alt="notif" className="anp-notif-img" />
    );
  }
  switch (type) {
    case "cart_notification":
      return <ShoppingCartIcon fontSize="small" />;
    case "price_plan_notification":
      return <LocalOfferIcon fontSize="small" />;
    case "other_services_notification":
      return <BuildIcon fontSize="small" />;
    default:
      return <NotificationsNoneIcon fontSize="small" />;
  }
};

const getTimeAgo = (dateStr) => {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  return `${Math.floor(diff / 86400)} days ago`;
};

const TABS = [
  { key: "all", label: "All" },
  { key: "read", label: "Read" },
  { key: "unread", label: "Unread" },
  { key: "archived", label: "Archived" },
];

const AllNotificationsPage = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const token = user?.token;
  const [activeTab, setActiveTab] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [counts, setCounts] = useState({ total: 0, unread: 0, archived: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 15,
    last_page: 1,
    has_more: false,
  });

  const searchTimeout = useRef(null);

  const fetchNotifications = useCallback(
    async (
      tab = activeTab,
      page = 1,
      searchVal = search,
      date = dateFilter,
    ) => {
      if (!token) return;
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("per_page", 15);
        params.set("page", page);
        if (tab !== "all") params.set("filter", tab);
        if (searchVal) params.set("search", searchVal);
        if (date) params.set("date", date);

        const res = await fetch(`${BASE_URL}/notifications?${params}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        const json = await res.json();
        if (json.success) {
          setNotifications(json.data.notifications);
          setCounts(json.data.counts);
          setFilteredTotal(json.data.pagination.total);
          setPagination(json.data.pagination);
        }
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    },
    [token, activeTab, search, dateFilter],
  );

  useEffect(() => {
    fetchNotifications(activeTab, currentPage, search, dateFilter);
    // eslint-disable-next-line
  }, [activeTab, currentPage, search, dateFilter]);

  // Debounced search
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearch(val);
      setCurrentPage(1);
    }, 500);
  };

  const handleDateChange = (e) => {
    setDateFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  // Mark one as read
  const markOneRead = async (id) => {
    try {
      await fetch(`${BASE_URL}/notifications/${id}/read`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, read_at: new Date().toISOString() } : n,
        ),
      );
      setCounts((c) => ({ ...c, unread: Math.max(0, c.unread - 1) }));
    } catch (err) {
      console.error(err);
    }
  };

  // Mark all read
  const markAllRead = async () => {
    try {
      await fetch(`${BASE_URL}/notifications/read-all`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          read_at: n.read_at || new Date().toISOString(),
        })),
      );
      setCounts((c) => ({ ...c, unread: 0 }));
      toast.success("All notifications marked as read");
    } catch (err) {
      console.error(err);
    }
  };

  // Archive
  const archiveNotification = async (id) => {
    try {
      await fetch(`${BASE_URL}/notifications/${id}/archive`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      setNotifications((prev) => prev.filter((n) => n.id !== id));
         setCounts((c) => ({
      ...c,
      // total: Math.max(0, c.total - 1),
      archived: (c.archived ?? 0) + 1,  
    }));
      toast.info("Notification archived");
    } catch (err) {
      console.error(err);
    }
  };

  // Unarchive
  const unarchiveNotification = async (id) => {
    try {
      await fetch(`${BASE_URL}/notifications/${id}/unarchive`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      setNotifications((prev) => prev.filter((n) => n.id !== id));
        setCounts((c) => ({
          ...c,
          archived: Math.max(0, (c.archived ?? 0) - 1), 
          // total: c.total + 1, 
        }));
      toast.info("Notification unarchived");
    } catch (err) {
      console.error(err);
    }
  };

  // Delete
  const deleteNotification = async (id) => {
    try {
      await fetch(`${BASE_URL}/notifications/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setCounts((c) => ({ ...c, total: Math.max(0, c.total - 1) }));
      toast.error("Notification deleted");
    } catch (err) {
      console.error(err);
    }
  };

  const handleNotificationClick = (n) => {
    if (!n.read_at) markOneRead(n.id);
      if (n.data?.data?.link) {
      navigate(n.data.data.link);
    }
  };

  const tabCount = (tab) => {
    if (tab === "all") return counts.total;
    if (tab === "unread") return counts.unread;
    if (tab === "read") return counts.read ?? 0;
    if (tab === "archived") return counts.archived ?? 0;
    return 0;
  };

  const totalPages = pagination.last_page || 1;

  return (
    <div className="anp-page">
      {/* Page Title */}
      <div className="anp-page-topbar">
        <div className="anp-card-header">
          <div className="anp-card-title">
            All Notification
            <span className="anp-count-badge">{filteredTotal}</span>
          </div>
        </div>
        <button className="anp-settings-btn">
          <SettingsOutlinedIcon fontSize="small" />
          Settings
        </button>
      </div>
      {/* Main Card */}
      <div className="anp-card ">
        {/* Filters Row */}
        <div className="anp-filters-row">
          <div className="anp-search-wrap">
            <SearchIcon className="anp-search-icon" />
            <input
              className="anp-search-input"
              placeholder="Search notifications..."
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
          <div className=" d-flex justify-content-center align-items-center gap-3">
            <div className="anp-date-wrap">
              <CalendarTodayOutlinedIcon className="anp-date-icon" />
              <input
                type="date"
                className="anp-date-input"
                value={dateFilter}
                onChange={handleDateChange}
              />
              {dateFilter && (
                <button
                  className="anp-date-clear"
                  onClick={() => {
                    setDateFilter("");
                    setCurrentPage(1);
                  }}
                >
                  ×
                </button>
              )}
            </div>
            <button
              className="anp-sort-btn"
              title="Reset filters"
              onClick={() => {
                setSearch("");
                setSearchInput("");
                setDateFilter("");
                setCurrentPage(1);
              }}
            >
              <SyncAltIcon fontSize="small" />
            </button>
          </div>
        </div>
        {/* Tabs + Mark All */}
        <div className="anp-tabs-row">
          <div className="anp-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                className={`anp-tab ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.label}
                <span
                  className={`anp-tab-count ${activeTab === tab.key ? "active" : ""}`}
                >
                  {tabCount(tab.key)}
                </span>
              </button>
            ))}
          </div>
          <button
            className="anp-mark-all-btn"
            onClick={markAllRead}
            disabled={counts.unread === 0}
          >
            <CheckCircleOutlineIcon fontSize="small" />
            Mark All As Read
          </button>
        </div>
        {/* Notifications List */}
        <div className="anp-list">
          {loading ? (
            <div className="anp-skeleton-wrap">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="anp-skeleton-row">
                  <div className="anp-sk-icon" />
                  <div className="anp-sk-body">
                    <div className="anp-sk-line short" />
                    <div className="anp-sk-line long" />
                  </div>
                  <div className="anp-sk-time" />
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="anp-empty">
              <NotificationsNoneIcon className="anp-empty-icon" />
              <p>No notifications found</p>
            </div>
          ) : (
            notifications.map((n) => {
              const isUnread = !n.read_at;
              return (
                <div
                  key={n.id}
                  className={`anp-row ${isUnread ? "unread" : "read"}`}
                  onClick={() => handleNotificationClick(n)}
                >
                  {/* Icon */}
                  <div className={`anp-row-icon ${n.type}`}>
                    {getNotificationIcon(n.type, n.data?.image)}
                  </div>

                  {/* Content */}
                  <div className="anp-row-content">
                    <div className="anp-row-title">{n.data?.title}</div>
                    <div
                      className="anp-row-body"
                      dangerouslySetInnerHTML={{
                        __html: n.data?.body?.replace(
                          /\*\*(.*?)\*\*/g,
                          "<strong>$1</strong>",
                        ),
                      }}
                    />
                  </div>

                  {/* Right side */}
                  <div className="anp-row-right">
                    <span className="anp-row-time">
                      {getTimeAgo(n.created_at)}
                    </span>
                    <div
                      className="anp-row-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {activeTab === "archived" ? (
                        <button
                          className="anp-action-btn unarchive"
                          title="Unarchive"
                          onClick={() => unarchiveNotification(n.id)}
                        >
                          <UnarchiveOutlinedIcon fontSize="small" />
                        </button>
                      ) : (
                        <button
                          className="anp-action-btn archive"
                          title="Archive"
                          onClick={() => archiveNotification(n.id)}
                        >
                          <ArchiveOutlinedIcon fontSize="small" />
                        </button>
                      )}
                      <button
                        className="anp-action-btn delete"
                        title="Delete"
                        onClick={() => deleteNotification(n.id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </button>
                    </div>
                  </div>

                  {/* Unread dot */}
                  {isUnread && <span className="anp-unread-dot" />}
                </div>
              );
            })
          )}
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="anp-pagination">
            <button
              className="anp-page-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              ‹ Prev
            </button>
            <div className="anp-page-nums">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  (p) =>
                    p === 1 ||
                    p === totalPages ||
                    Math.abs(p - currentPage) <= 2,
                )
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === "..." ? (
                    <span key={`ellipsis-${i}`} className="anp-page-ellipsis">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      className={`anp-page-num ${currentPage === p ? "active" : ""}`}
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </button>
                  ),
                )}
            </div>
            <button
              className="anp-page-btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
};;

export default AllNotificationsPage;
